import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, Typography, useTheme, InputBase, IconButton, Avatar, ButtonGroup } from "@mui/material";
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarExport } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config";

const Verification = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");

  const fetchPendingDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/admin/drivers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken, page, search, verified: 0, limit: 20 }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch pending drivers");
      }

      const data = await response.json();
      setDrivers(data.data || []);
      setRowCount(data.total || (data.data ? data.data.length : 0));
    } catch (error) {
      console.error("Error fetching verification list:", error);
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchPendingDrivers();
  }, [fetchPendingDrivers]);

  const handleVerify = async (driverId, status) => {
    try {
      const authToken = sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/admin/verify-unverify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken, driverId, verified: status }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(status === 1 ? "Chauffeur validé avec succès (1 mois offert activé) !" : "Dossier rejeté.");
        fetchPendingDrivers();
      } else {
        toast.error(data.message || "Erreur lors de la validation");
      }
    } catch (err) {
      console.error("Erreur vérification:", err);
      toast.error("Erreur réseau");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.6 },
    {
      field: "picture",
      headerName: "Photo",
      flex: 0.6,
      renderCell: (params) => {
        const url = params.value?.startsWith("http")
          ? params.value
          : (params.value ? `${API_BASE_URL}/uploads/drivers/${params.value}` : null);
        return <Avatar src={url} sx={{ width: 36, height: 36 }} />;
      },
    },
    {
      field: "name",
      headerName: "Chauffeur",
      flex: 1.2,
      renderCell: (params) => (
        <Box>
          <Typography fontWeight="bold" color={colors.grey[100]}>
            {params.row.name} {params.row.fname}
          </Typography>
          <Typography variant="caption" color={colors.grey[300]}>
            @{params.row.username} • {params.row.phone}
          </Typography>
        </Box>
      ),
    },
    { field: "email", headerName: "Email", flex: 1.1 },
    {
      field: "car",
      headerName: "Véhicule",
      flex: 0.8,
      renderCell: (params) => {
        const cars = { 1: "Classic", 2: "Confort", 3: "VIP", 4: "Woman", 5: "Express" };
        return cars[params.value] || "Classic";
      },
    },
    {
      field: "created_at",
      headerName: "Date Inscription",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[200]}>
          {params.value ? new Date(params.value).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
          }) : "-"}
        </Typography>
      ),
    },
    {
      field: "verification",
      headerName: "Documents (Permis/Carte)",
      flex: 1,
      renderCell: (params) => {
        if (!params.value) {
          return <Typography variant="caption" color={colors.grey[400]}>Non fourni</Typography>;
        }
        const docUrl = params.value.startsWith("http")
          ? params.value
          : `${API_BASE_URL}/uploads/verification/${params.value}`;
        return (
          <Button
            href={docUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ color: colors.blueAccent[300], borderColor: colors.blueAccent[500], fontSize: "11px" }}
          >
            Télécharger
          </Button>
        );
      },
    },
    {
      field: "actions",
      headerName: "Décision",
      flex: 1.3,
      renderCell: (params) => (
        <ButtonGroup size="small" variant="contained">
          <Button
            onClick={() => handleVerify(params.row.id, 1)}
            startIcon={<CheckCircleOutlineIcon />}
            sx={{ backgroundColor: colors.greenAccent[600], "&:hover": { backgroundColor: colors.greenAccent[700] }, fontSize: "11px" }}
          >
            Valider
          </Button>
          <Button
            onClick={() => handleVerify(params.row.id, 2)}
            startIcon={<HighlightOffIcon />}
            sx={{ backgroundColor: "#d32f2f", "&:hover": { backgroundColor: "#b71c1c" }, fontSize: "11px" }}
          >
            Rejeter
          </Button>
        </ButtonGroup>
      ),
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </GridToolbarContainer>
    );
  }

  return (
    <Box m="20px">
      <Header title="VÉRIFICATION DES CHAUFFEURS" subtitle="Examen et validation des dossiers d'inscription chauffeur" />

      <Box display="flex" backgroundColor={colors.primary[400]} borderRadius="3px" mt="15px">
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ml: 2, flex: 1 }}
          placeholder="Rechercher par nom, téléphone ou email..."
        />
        <IconButton type="button" sx={{ p: 1 }}>
          <SearchIcon />
        </IconButton>
      </Box>

      <Box
        m="20px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
          "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${colors.grey[100]} !important` },
        }}
      >
        <DataGrid
          rows={drivers}
          columns={columns}
          slots={{ toolbar: CustomToolbar }}
          pagination
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          onPaginationModelChange={(newModel) => setPage(newModel.page + 1)}
        />
      </Box>
    </Box>
  );
};

export default Verification;
